// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

/**
 * ClientActivityTab
 *
 * Client-scoped variant of `ActivityLogPage`. Renders the same filter strip +
 * unified feed of alerts/insights but scoped to a single profile (the client's
 * parent profile), so the tab only shows activity relevant to the client being
 * viewed instead of brokerage-wide events.
 *
 * Relies on `useDashboardAlerts` / `useDashboardInsights`, which forward the
 * `profileId` query param to the Alerts & Insights microservice. Filter logic
 * is shared with the full `ActivityLogPage` via `filterActivityItems` in
 * `types/activityLog.ts`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBanner } from '@loa/ui-components';
import { useDashboardAlerts, useDashboardInsights } from '../../hooks/useDashboard';
import {
  mergeAlertsAndInsights,
  filterActivityItems,
} from '../../types/activityLog';
import type {
  ActivityLogFilters,
  ActivityTypeFilter,
  UnifiedActivityItem,
} from '../../types/activityLog';
import { ActivityFilters } from '../activity/ActivityFilters';
import { ActivityItemCard } from '../activity/ActivityItemCard';
import { ActivityLogSkeleton } from '../activity/ActivityLogSkeleton';
import './ClientActivityTab.css';

type ClientActivityTabProps = Readonly<{
  /** Profile ID used to scope the alerts/insights query to this client's profile. */
  profileId: string;
}>;

export function ClientActivityTab({ profileId }: ClientActivityTabProps) {
  const navigate = useNavigate();

  const {
    data: alertsData,
    loading: alertsLoading,
    error: alertsError,
  } = useDashboardAlerts({ profileId, limit: 100 });

  const {
    data: insightsData,
    loading: insightsLoading,
    error: insightsError,
  } = useDashboardInsights({ profileId, limit: 100 });

  const [filters, setFilters] = useState<ActivityLogFilters>({
    typeFilter: 'all',
    startDate: undefined,
    endDate: undefined,
  });

  const allItems = useMemo(
    () =>
      mergeAlertsAndInsights(
        alertsData?.alerts ?? [],
        insightsData?.insights ?? []
      ),
    [alertsData, insightsData]
  );

  const filteredItems = useMemo(
    () => filterActivityItems(allItems, filters),
    [allItems, filters]
  );

  const updateFilters = useCallback(
    (key: keyof ActivityLogFilters, value: string | undefined) => {
      setFilters((prev) => ({
        ...prev,
        [key]: key === 'typeFilter' ? ((value as ActivityTypeFilter) || 'all') : value,
      }));
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilters({ typeFilter: 'all', startDate: undefined, endDate: undefined });
  }, []);

  const handleItemClick = useCallback(
    (item: UnifiedActivityItem) => {
      if (item.profileId) {
        navigate(`/relationships/${item.profileId}`);
      }
    },
    [navigate]
  );

  const loading = alertsLoading || insightsLoading;
  const errorMessage = (() => {
    if (alertsError && insightsError) return 'Failed to load Alerts and Insights';
    if (alertsError) return 'Failed to load Alerts';
    if (insightsError) return 'Failed to load Insights';
    return null;
  })();

  const hasActiveFilters =
    filters.typeFilter !== 'all' || !!filters.startDate || !!filters.endDate;

  return (
    <div className="client-activity-tab">
      <div className="client-activity-tab__header">
        <h3 className="client-activity-tab__title">Activity</h3>
      </div>

      <ActivityFilters
        filters={filters}
        onChange={updateFilters}
        onClear={handleClearFilters}
        disabled={loading}
      />

      {errorMessage && (
        <StatusBanner
          variant="error"
          message={errorMessage}
          visible
          position="inline"
        />
      )}

      {(() => {
        if (loading) return <ActivityLogSkeleton />;
        if (filteredItems.length === 0) {
          const emptyText = hasActiveFilters
            ? 'No activity matches your filter criteria. Try adjusting your filters.'
            : 'No recent activity to show';
          return (
            <div className="client-activity-tab__empty">
              <span className="client-activity-tab__empty-text">{emptyText}</span>
            </div>
          );
        }
        return (
          <div className="client-activity-tab__list">
            {filteredItems.map((item) => (
              <ActivityItemCard key={item.id} item={item} onClick={handleItemClick} />
            ))}
          </div>
        );
      })()}
    </div>
  );
}
